import { ElementProps } from '@/components/sections/builder/types';
import {
  HeroButtonGroupElementDataType,
  HeroElementDataType,
} from '@/components/sections/builder/elements/body/hero/types';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import InputTab from '@/components/sections/builder/settings/input-tab';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import { Input } from '@/components/ui/input';
import { useBuilderStore } from '@/stores/builder';
import clsx from 'clsx';
import { shallow } from 'zustand/shallow';
import { GalleryHorizontal } from 'lucide-react';
import InputLink from '@/components/sections/builder/settings/input-link';
import { isEmpty } from '@/lib/utils';
import Link from 'next/link';

const defaultData: HeroButtonGroupElementDataType = {
  name: 'Button Group',
  button1Label: 'Get Started',
  button2Label: 'Contact Sales',
  button1Type: 'primary',
  button2Type: 'secondary',
  gap: 12,
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as HeroButtonGroupElementDataType;
  const parentData = (element.parentData || {}) as HeroElementDataType;

  const isExternal1 = data.button1Link?.includes('http');
  const isExternal2 = data.button2Link?.includes('http');

  return (
    <div className='hero-button-padding'>
      {!!data.button1Label && (
        <Link
          href={
            isExternal1
              ? data.button1Link || '#'
              : `/${(data.button1Link || '').replace(/^\/+/, '')}`
          }
          className={clsx(
            'button-gap theme_button',
            !isEmpty(parentData.colorScheme) && 'button-color-scheme',
            data.button1Type === 'primary'
              ? 'focus:ring-primary-300 inline-flex items-center justify-center rounded-lg bg-primary px-5 py-3 text-center text-base font-medium text-white hover:bg-primary focus:ring-4'
              : 'inline-flex items-center justify-center rounded-lg border border-gray-300 px-5 py-3 text-center text-base font-medium text-gray-900 hover:bg-gray-100 focus:ring-4 focus:ring-gray-100'
          )}
          target={isExternal1 ? '_blank' : undefined}
          rel={isExternal1 ? 'noopener noreferrer' : undefined}
          data-animation='true'
        >
          {data.button1Label}
        </Link>
      )}
      {!!data.button2Label && (
        <Link
          href={
            isExternal2
              ? data.button2Link || '#'
              : `/${(data.button2Link || '').replace(/^\/+/, '')}`
          }
          className={clsx(
            'theme_button',
            !isEmpty(parentData.colorScheme) && 'button-color-scheme',
            data.button2Type === 'primary'
              ? 'focus:ring-primary-300 inline-flex items-center justify-center rounded-lg bg-primary px-5 py-3 text-center text-base font-medium text-white hover:bg-primary focus:ring-4'
              : 'inline-flex items-center justify-center rounded-lg border border-gray-300 px-5 py-3 text-center text-base font-medium text-gray-900 hover:bg-gray-100 focus:ring-4 focus:ring-gray-100'
          )}
          target={isExternal2 ? '_blank' : undefined}
          rel={isExternal2 ? 'noopener noreferrer' : undefined}
          data-animation='true'
        >
          {data.button2Label}
        </Link>
      )}
      <style jsx>
        {`
          .button-gap {
            margin-right: ${data.gap || 0}px;
          }
          @media (min-width: 1024px) {
            .hero-button-padding {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${data.padding?.left || 0}px;
              padding-right: ${data.padding?.right || 0}px;
            }
          }
          .button-color-scheme {
            color: ${parentData?.colorScheme?.btnSolidlabel} !important;
            background-color: ${parentData?.colorScheme?.btnSolidBackgroud} !important;
          }
        `}
      </style>
    </div>
  );
};

export const ElementPreviewIcon = GalleryHorizontal;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = (element.data || defaultData) as HeroButtonGroupElementDataType;

  return (
    <div>
      <div className='mb-4 font-bold'>Button 1</div>
      <Input
        onChange={(e) =>
          builder.setChildData(element.parentId as string, element.id, {
            button1Label: e.target.value,
          })
        }
        name='button1Label'
        type='text'
        size={100}
        defaultValue={data.button1Label}
        className='w-full'
        placeholder='Label'
      />
      <div className='mb-4 mt-2 text-xs'>Type</div>
      <InputTab
        grid={2}
        defaultValue={data.button1Type}
        options={[
          { label: 'Primary', value: 'primary' },
          { label: 'Secondary', value: 'secondary' },
        ]}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            button1Type: v,
          })
        }
      />
      <div className='mb-2 mt-2'>Link</div>
      <InputLink
        name='url_path_1'
        placeholder='Search or enter a link'
        onChange={(value) => {
          builder.setChildData(element.parentId as string, element.id, {
            button1Link: value,
          });
        }}
        value={data.button1Link as string}
      />
      <div className='mb-4 mt-4 font-bold'>Button 2</div>
      <Input
        onChange={(e) =>
          builder.setChildData(element.parentId as string, element.id, {
            button2Label: e.target.value,
          })
        }
        name='button2Label'
        type='text'
        size={100}
        defaultValue={data.button2Label}
        className='w-full'
        placeholder='Label'
      />
      <div className='mb-4 mt-2 text-xs'>Type</div>
      <InputTab
        grid={2}
        defaultValue={data.button2Type}
        options={[
          { label: 'Primary', value: 'primary' },
          { label: 'Secondary', value: 'secondary' },
        ]}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            button2Type: v,
          })
        }
      />
      <div className='mb-2 mt-2'>Link</div>
      <InputLink
        name='url_path_2'
        placeholder='Search or enter a link'
        onChange={(value) => {
          builder.setChildData(element.parentId as string, element.id, {
            button2Link: value,
          });
        }}
        value={data.button2Link as string}
      />
      <div className='mb-4 mt-4 font-bold'>Button Gap</div>
      <InputSlider
        value={data.gap || 0}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            gap: val,
          })
        }
      />
      <PaddingSetting
        value={data.padding}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            padding: v,
          })
        }
      />
    </div>
  );
}

const HeroButtonGroup = {
  type: 'hero_button_group',
  group: 'hero',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default HeroButtonGroup;
